'use client';

/**
 * ConfirmDialog — Phase A7 destructive-action confirmation primitive.
 *
 * Standard pattern for "Are you sure?" prompts. Two variants:
 *
 * 1. `simple` (default) — title + description + Cancel/Confirm buttons.
 *    Use for low-stakes confirmations ("Discard draft?", "Mark resolved?").
 *
 * 2. `destructive` — same shape + red Confirm button + optional
 *    `typeToConfirmWord` requiring the user to type a specific word
 *    (typically the entity name) before the button enables. Use for
 *    high-stakes destructive actions (Delete client, Revoke access,
 *    Cancel contract).
 *
 * Composes on top of the existing AlertDialog primitive in this package
 * — the dialog chrome, focus management, escape-to-close, and overlay
 * are owned by AlertDialog. ConfirmDialog adds the type-to-confirm gate
 * and the standard button styling.
 *
 * When to use which (Phase A7 decision matrix):
 * - Drawer    → long form, multi-step workflow, contextual edit
 * - Modal     → short confirmation, single decision (this primitive)
 * - Toast     → action confirmation, time-bounded info
 * - Confirm   → destructive actions, "type X to confirm" gate
 */

import React, { useId, useState } from 'react';
import { Dialog } from '../dialog';
import { cn } from '../../lib/utils';

export interface ConfirmDialogProps {
  /** Controlled open state. */
  open: boolean;
  /** Called when the dialog closes (Cancel, overlay click, Esc). */
  onClose: () => void;
  /**
   * Called when the user confirms (clicks the primary button OR presses
   * Enter while the input is valid). The dialog does NOT auto-close —
   * the consumer is expected to call `onClose()` once the action either
   * completes or surfaces an error.
   */
  onConfirm: () => void;
  /** Title — "Delete Bob's account?", "Discard draft?". */
  title: string;
  /** Body copy explaining what's about to happen. */
  description?: React.ReactNode;
  /** Label for the primary action. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label for the cancel action. Defaults to "Cancel". */
  cancelLabel?: string;
  /**
   * Variant — `destructive` styles the confirm button red and enables
   * the optional type-to-confirm gate.
   */
  variant?: 'simple' | 'destructive';
  /**
   * When set on a `destructive` variant, the user must type this exact
   * string before the confirm button enables (case-sensitive). Typical
   * pattern: pass the entity name ("Bob Smith", "Madea Park").
   */
  typeToConfirmWord?: string;
  /**
   * Loading flag for in-flight confirms — disables the primary button
   * + shows a "…" suffix. The dialog stays open while loading; the
   * consumer must call `onClose()` to dismiss.
   */
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'simple',
  typeToConfirmWord,
  loading,
}: ConfirmDialogProps) {
  const [typedValue, setTypedValue] = useState('');
  const inputId = useId();
  // Reset typed value whenever the dialog re-opens to avoid carrying
  // a stale entry across sessions.
  const previousOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (open && !previousOpenRef.current) {
      setTypedValue('');
    }
    previousOpenRef.current = open;
  }, [open]);

  const requiresTyping = variant === 'destructive' && !!typeToConfirmWord;
  const typingValid = !requiresTyping || typedValue === typeToConfirmWord;
  const canConfirm = !loading && typingValid;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canConfirm) {
      e.preventDefault();
      onConfirm();
    }
  };

  // Codex Phase A Sprint 3 pass 1 P2: forbid dismissal while a
  // confirmation is in flight — otherwise a user can Esc / click-out
  // mid-action and the parent never knows whether to keep waiting.
  const safeClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={safeClose}
      title={title}
      size="sm"
      closeOnBackdrop={!loading}
      closeOnEscape={!loading}
    >
      <div className="space-y-3" data-confirm-dialog>
        {description && (
          <div className="text-[12.5px] leading-relaxed text-text-body">{description}</div>
        )}
        {requiresTyping && (
          <div className="space-y-1.5">
            <label htmlFor={inputId} className="block text-[11.5px] font-medium text-statura-ink">
              Type{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px] text-statura-ink">
                {typeToConfirmWord}
              </code>{' '}
              to confirm
            </label>
            <input
              id={inputId}
              type="text"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              aria-required="true"
              aria-invalid={requiresTyping && !typingValid && typedValue.length > 0}
              className={cn(
                'h-9 w-full rounded-md border bg-white px-2.5 text-xs text-statura-ink placeholder:text-text-muted',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible/40',
                typingValid && typedValue.length > 0
                  ? 'border-emerald-300 focus:border-emerald-500'
                  : !typingValid && typedValue.length > 0
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-slate-200 focus:border-statura-green-accessible',
                loading && 'cursor-not-allowed opacity-60',
              )}
            />
          </div>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-statura-ink hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible disabled:cursor-not-allowed disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          {/*
            Codex Phase A Sprint 3 pass 1 P2: when the type-to-confirm
            gate is unsatisfied we use `aria-disabled` + a no-op click
            instead of the native `disabled` attribute so the Dialog's
            focus trap keeps the button in its tabbable list. Tab from
            Cancel still lands here (loops back to the input), not on
            an element outside the modal. The native `disabled`
            attribute IS still used during `loading` because a loading
              state also stays focusable but inert; otherwise focus can
              leave the Dialog while the request is in flight.
           */}
          <button
            type="button"
            onClick={() => canConfirm && !loading && onConfirm()}
            aria-disabled={!canConfirm || loading}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white focus:outline-none focus-visible:ring-2',
              variant === 'destructive'
                ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500'
                : 'bg-statura-green-accessible hover:bg-statura-green-accessible-hover focus-visible:ring-statura-green-accessible',
              (!canConfirm || loading) && 'cursor-not-allowed opacity-50 hover:bg-red-600',
              (!canConfirm || loading) &&
                variant !== 'destructive' &&
                'hover:bg-statura-green-accessible',
            )}
          >
            {confirmLabel}
            {loading ? '…' : ''}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
